import { Component, OnDestroy, OnInit } from "@angular/core";
import { ColDef, ColGroupDef, FirstDataRenderedEvent, GridApi, GridOptions, GridReadyEvent, GridSizeChangedEvent, ValueFormatterParams } from "ag-grid-community";
import { Subscription } from "rxjs";
import { Language } from "../models/language.model";
import { Placeholder } from "../models/placeholder.model";
import { MomentPipe } from "../pipes/moment.pipe";
import { TranslatePipe } from "../pipes/translate.pipe";
import { LocalizationService } from "../services/localization.service";

@Component({
    template: ''
})
export class AgGridBaseComponent implements OnInit, OnDestroy {

    public locale: string;

    private _gridApi: GridApi;
    private _gridOptions: GridOptions;
    private localeSubscription: Subscription;

    constructor(
        protected localizationService: LocalizationService,
        protected momentPipe: MomentPipe,
        protected translatePipe: TranslatePipe
    ) { }

    ngOnInit(): void {
        this.localeSubscription = this.localizationService.getLanguage().subscribe((language: Language) => this.locale = language.getCode());
    }

    ngOnDestroy(): void {
        this.localeSubscription?.unsubscribe();
    }

    get allRowCount(): number {
        return this._gridOptions?.rowData?.length ?? 0;
    }

    get displayedRowCount(): number {
        return this._gridApi?.getDisplayedRowCount() ?? 0;
    }

    get gridApi(): GridApi {
        return this._gridApi;
    }

    get gridOptions(): GridOptions {
        return this._gridOptions;
    }

    get isLoading(): boolean {
        return this.localizationService.isLoadingTranslations();
    }

    protected initializeGridOptions(columnDefs?: (ColDef | ColGroupDef)[]): void {
        this._gridOptions = {
            animateRows: true,
            columnDefs,
            defaultColDef: {
                filterParams: {
                    browserDatePicker: false
                },
                floatingFilter: true,
                floatingFilterComponentParams: {
                    suppressFilterButton: true
                },
                resizable: true,
                sortable: true,
                suppressMenu: true
            },
            overlayLoadingTemplate: this.translatePipe.transform('D41', this.locale),
            overlayNoRowsTemplate: this.translatePipe.transform('T30', this.locale),
            rowHeight: 30,
            onFirstDataRendered(event: FirstDataRenderedEvent) {
                event.api.sizeColumnsToFit();
            },
            onGridReady: (event: GridReadyEvent) => {
                this._gridApi = event.api;
            },
            onGridSizeChanged: (event: GridSizeChangedEvent) => {
                event.api.sizeColumnsToFit();
            }
        };
    }

    protected createHeader(code: string, placeholders?: Placeholder[]): string {
        const header = this.translatePipe.transform(code, this.locale, placeholders);
        const headerWords = header?.split(' ');

        if (headerWords.length === 2 && headerWords[1].length > 3) {
            return `${headerWords[0]}\n${headerWords[1]}`;
        }

        if (headerWords.length > 2) {
            const middleIndex = Math.floor(headerWords.length / 2);
            const firstPart = headerWords.slice(0, middleIndex).join(' ');
            const secondPart = headerWords.slice(middleIndex).join(' ');
            return `${firstPart}\n${secondPart}`;
        }

        return header;
    }

    protected formatDate(params: ValueFormatterParams): string {
        const date: string | null = this.momentPipe.transform(params.value, 'YYYY-MM-DD', this.locale);
        return date ? date : params.value;
    }

    protected formatDecimal(params: ValueFormatterParams): string {
        if (params.value == null)
            return params.value;
        return (Math.round(params.value * 100) / 100).toFixed(2);
    }

    protected formatPercentage(params: ValueFormatterParams): string {
        if (params.value == null)
            return params.value;
        return `${params.value}%`;
    }

}
